import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  loginAction,
  logoutAction,
  getCurrentUserAction,
  validateTokenAction,
  refreshTokenAction,
  getUserRolesAction,
  getAllUsersAction,
  registerUserAction,
  updateUserRoleAction,
  toggleUserStatusAction,
  requestPasswordResetAction,
  resetPasswordAction,
  changePasswordAction,
  updateUserProfileAction,
  initializeAuthAction
} from "../actions/authActions";
import {
  setError as SetError,
  selectAuthStatus,
  selectAuthError,
  selectAuthLoading,
  selectIsAuthenticated,
  selectToken,
  selectUser,
  selectRoles,
  selectAllUsers,
  selectRememberMe,
  selectInitialized,
  selectSessionExpiry,
  selectRefreshAttempts,
  selectIsSessionExpired,
  selectPasswordResetLoading,
  selectPasswordResetError,
  selectProfileUpdateLoading,
  selectProfileUpdateError,
  // Derived selectors
  selectUserRole,
  selectUserPermissions,
  selectUserId,
  selectUserClientId,
  selectUserName,
  selectUserEmail,
  selectUserDisplayName,
  selectUserInitials,
  // Role selectors
  selectIsSuperAdmin,
  selectIsClientAdmin,
  selectIsClientHRManager,
  selectIsClientInterviewer,
  selectIsClientViewer,
  // Legacy role selectors
  selectIsAdmin,
  selectIsHRManager,
  selectIsRecruiter,
  selectIsViewer
} from "../slices/authSlice";
import useClient from "./useClient";

const useAuth = () => {
  const dispatch = useDispatch();
  
  // Auth state selectors
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const loading = useSelector(selectAuthLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);
  const roles = useSelector(selectRoles);
  const allUsers = useSelector(selectAllUsers);
  const rememberMe = useSelector(selectRememberMe);
  const initialized = useSelector(selectInitialized);
  
  console.log('🔍 useAuth: State updated', { 
    status, 
    loading, 
    isAuthenticated, 
    initialized, 
    error,
    token: !!token,
    user: !!user
  });
  const sessionExpiry = useSelector(selectSessionExpiry);
  const refreshAttempts = useSelector(selectRefreshAttempts);
  const isSessionExpired = useSelector(selectIsSessionExpired);
  const passwordResetLoading = useSelector(selectPasswordResetLoading);
  const passwordResetError = useSelector(selectPasswordResetError);
  const profileUpdateLoading = useSelector(selectProfileUpdateLoading);
  const profileUpdateError = useSelector(selectProfileUpdateError);
  
  // Derived selectors
  const userRole = useSelector(selectUserRole);
  const userPermissions = useSelector(selectUserPermissions);
  const userId = useSelector(selectUserId);
  const userClientId = useSelector(selectUserClientId);
  const userName = useSelector(selectUserName);
  const userEmail = useSelector(selectUserEmail);
  const userDisplayName = useSelector(selectUserDisplayName);
  const userInitials = useSelector(selectUserInitials);
  
  // Role selectors
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const isClientAdmin = useSelector(selectIsClientAdmin);
  const isClientHRManager = useSelector(selectIsClientHRManager);
  const isClientInterviewer = useSelector(selectIsClientInterviewer);
  const isClientViewer = useSelector(selectIsClientViewer);
  
  // Legacy role selectors
  const isAdmin = useSelector(selectIsAdmin);
  const isHRManager = useSelector(selectIsHRManager);
  const isRecruiter = useSelector(selectIsRecruiter);
  const isViewer = useSelector(selectIsViewer);
  
  // Get client context
  const { currentClient } = useClient();
  
  // Get client ID based on user role
  const clientId = userRole === 'super_admin' 
    ? (currentClient?.client_id || null) 
    : userClientId;
  
  // Error handling
  const setError = (error) => {
    return dispatch(SetError(error));
  };

  // Initialize auth state
  const initializeAuth = useCallback(async () => {
    console.log("🔍 useAuth - initializeAuth called with:", { clientId, user: !!user });
    return dispatch(initializeAuthAction(clientId, user));
  }, [dispatch, clientId, user]);

  // Core auth operations
  const loginUser = async (credentials) => {
    return dispatch(loginAction(credentials));
  };

  const logoutUser = async () => {
    return dispatch(logoutAction(clientId, user));
  };

  const fetchCurrentUser = async () => {
    return dispatch(getCurrentUserAction(clientId, user));
  };

  const validateUserToken = async () => {
    return dispatch(validateTokenAction(clientId, user));
  };

  const refreshAuthentication = async () => {
    return dispatch(refreshTokenAction(clientId, user, refreshAttempts, rememberMe));
  };

  const fetchUserRoles = async () => {
    return dispatch(getUserRolesAction(clientId, user));
  };

  const fetchAllUsers = async () => {
    return dispatch(getAllUsersAction(clientId, user));
  };

  const registerNewUser = async (userData) => {
    return dispatch(registerUserAction(userData, clientId, user));
  };

  const updateUserRole = async (userId, role) => {
    return dispatch(updateUserRoleAction(userId, role, clientId, user));
  };

  const toggleUserStatus = async (userId) => {
    return dispatch(toggleUserStatusAction(userId, clientId, user));
  };

  const requestPasswordReset = async (email) => {
    return dispatch(requestPasswordResetAction(email, user));
  };

  const resetPassword = async (token, newPassword) => {
    return dispatch(resetPasswordAction(token, newPassword, user));
  };

  const changePassword = async (currentPassword, newPassword) => {
    return dispatch(changePasswordAction(currentPassword, newPassword, clientId, user));
  };

  const updateUserProfile = async (profileData) => {
    return dispatch(updateUserProfileAction(profileData, clientId, user));
  };

  // Permission checking functions
  const hasPermission = (permission) => {
    if (!userPermissions) return false;
    
    // Check for wildcard permission
    if (userPermissions.includes('*')) return true;
    
    return userPermissions.includes(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (!userPermissions) return false;
    
    // Check for wildcard permission
    if (userPermissions.includes('*')) return true;
    
    return permissionList.some(permission => userPermissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!userPermissions) return false;
    
    // Check for wildcard permission
    if (userPermissions.includes('*')) return true;
    
    return permissionList.every(permission => userPermissions.includes(permission));
  };

  // Role checking functions
  const hasRole = (role) => {
    if (!userRole) return false;
    return userRole === role;
  };

  const hasAnyRole = (roles) => {
    if (!userRole) return false;
    return roles.includes(userRole);
  };

  // Session management
  const getSessionStatus = () => {
    if (!isAuthenticated || !sessionExpiry) {
      return { status: 'inactive', timeLeft: 0 };
    }

    const timeLeft = sessionExpiry - Date.now();
    const minutesLeft = Math.floor(timeLeft / (1000 * 60));

    if (timeLeft <= 0) {
      return { status: 'expired', timeLeft: 0 };
    } else if (timeLeft <= 5 * 60 * 1000) { // 5 minutes
      return { status: 'expiring', timeLeft, minutesLeft };
    } else {
      return { status: 'active', timeLeft, minutesLeft };
    }
  };

  const sessionStatus = getSessionStatus();

  return {
    // State
    status,
    error,
    loading,
    isAuthenticated,
    token,
    user,
    roles,
    allUsers,
    rememberMe,
    initialized,
    sessionExpiry,
    refreshAttempts,
    isSessionExpired,
    passwordResetLoading,
    passwordResetError,
    profileUpdateLoading,
    profileUpdateError,
    
    // Derived state
    userRole,
    userPermissions,
    userId,
    userClientId,
    userName,
    userEmail,
    userDisplayName,
    userInitials,
    
    // Role state
    isSuperAdmin,
    isClientAdmin,
    isClientHRManager,
    isClientInterviewer,
    isClientViewer,
    isAdmin,
    isHRManager,
    isRecruiter,
    isViewer,
    
    // Session state
    sessionStatus,
    isSessionActive: sessionStatus.status === 'active',
    isSessionExpiring: sessionStatus.status === 'expiring',
    sessionMinutesLeft: sessionStatus.minutesLeft,
    canRefreshToken: refreshAttempts < 3,
    
    // Actions
    initializeAuth,
    loginUser,
    logoutUser,
    fetchCurrentUser,
    validateUserToken,
    refreshAuthentication,
    fetchUserRoles,
    fetchAllUsers,
    registerNewUser,
    updateUserRole,
    toggleUserStatus,
    requestPasswordReset,
    resetPassword,
    changePassword,
    updateUserProfile,
    
    // Permission checking
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    // Role checking
    hasRole,
    hasAnyRole,
    
    // Error handling
    setError,
    
    // Client context
    clientId
  };
};

export { useAuth };
